import React from 'react';
import Papa from 'papaparse';
import { useSelector } from 'react-redux';
import differenceBy from 'lodash/differenceBy';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import intersectionBy from 'lodash/intersectionBy';
import isEqual from 'lodash/isEqual';
import concat from 'lodash/concat';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import delay from 'lodash/delay';
import { axios } from '../../../../services/axios';
import { downloadCSV } from '../../../../utils/utils';
import { isEmpty, startsWith } from 'lodash';
import appConfig from '../../../../config.json';
import Cookies from 'js-cookie';
import { number } from 'prop-types';
import { PlatformEnums } from '../../../../utils/platforms';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
// Fallback used when the selectedPlatformId cookie isn't set for 8x8 flows.
export const getPlatformId = () =>
    Cookies.get('selectedPlatformId') || PlatformEnums.EIGHTBYEIGHT;

export const IGNORED_MESSAGE = 'Ignored, no changes detected.';
export const SUCCESS_MESSAGE = 'Success.';
export const CSV_TYPE = 'text/csv';
export const CSV_TYPE_FX = 'application/vnd.ms-excel';
export const CSV_TYPE_ERROR = 'File must be .csv type';
let VERIFICATION_CHECKS = [
    {
        label: 'Received CSV',
        check: false,
        current: true,
        completionCounter: 0,
        total: 1,
    },
    {
        label: 'Fetching Company Data',
        check: false,
        current: false,
        completionCounter: 0,
        total: 4,
    },
    {
        label: 'Converting CSV',
        check: false,
        current: false,
        completionCounter: 0,
        total: 3,
    },
    {
        label: 'Verifying Users List and Status',
        check: false,
        current: false,
        completionCounter: 0,
        total: 5,
    },
    {
        label: 'Verifying Numbers List and Status',
        check: false,
        current: false,
        completionCounter: 0,
        total: 5,
    },
    {
        label: 'Verifying Dial Plan List',
        check: false,
        current: false,
        completionCounter: 0,
        total: 2,
    },
    {
        label: 'Verifying Voice Policy List',
        check: false,
        current: false,
        completionCounter: 0,
        total: 2,
    },
    // {
    //     label: 'Verifying Plan List',
    //     check: false,
    //     current: false,
    //     completionCounter: 0,
    //     total: 1,
    // },
];

const DR_TRUNK_TYPE = 'MS Direct Routing';
const DR_SERVICE_TYPE = 'DirectRouting';
const DR_GCCH_SERVICE_TYPE = 'DirectRoutingGCCH';
const DEFAULT_JSON_DATA = {
    errors: [],
    ignored: false,
    attributesUpdateOnly: false,
};
const USER_DNE_ERROR = 'This user does not exist in your company.';
const USER_NOT_LICENSED_ERROR = 'This user is not licensed.';
const NUMBER_DNE_ERROR = 'This number does not exist in your company.';
const DIALPLAN_DNE_ERROR = 'This dial plan does not exist in your company.';
const VP_DNE_ERROR = 'This voice policy does not exist in your company.';
const USER_NOT_OK_ERROR = 'This user is not in OK status.';
const NUMBER_NOT_OK_ERROR = 'This number is not in OK status.';
const NO_DATA_FOUND_ERROR = "We didn't detect any changes.";
const UPN_IS_INVALID_ERROR = 'This UPN does not exist in your TCAP system.';
const MANAGED_BY_INVALID_ERROR =
    'Cannot change Telephone Number / Dial Plan / Voice Policy for this user, since they are not managed by TCAP.';
const CALL_VALUE_INCONSISTENT_ERROR =
    'Cannot partially update calling values. Please set all values, or none.';
const UNLICENSED_USERS_INVALID_CHANGES_ERROR =
    'This user is unlicensed. You should not change their Telephone Number / Dial Plan / Voice Policy / Plan. Please license them first.';
const NUMBER_IS_NOT_DR_ERROR =
    'The number you are changing to is not using a Direct Routing Trunk, hence they do not need a Dial Plan and Voice Policy. Please remove their Dial Plan and Voice Policies values to proceed.';
const PLAN_NAME_ERROR =
    'Invalid plan name. Please make sure it is spelled correctly.';
const PLAN_PREFIX_ERROR = 'Prefix on plan does not match number.';
const PLAN_NUMBER_ERROR =
    'Cannot set a plan against a user without a number allocated.';
const PLAN_PERMISSION_ERROR =
    'Plans are only configurable by a partner. Please contact your partner.';
const FIELDS_TO_COMPARE = [
    //fields that can be updated from CSV importer
    'telephoneNumber',
    'dialPlan',
    'voicePolicy',
    'managedBy',
    'plan',
    'attribute1',
    'attribute2',
    'attribute3',
];

// Not every platform's CSV carries every field above (e.g. resource accounts
// have no Dial Plan/Plan column, Zoom/8x8 have neither) — comparing a field
// the CSV never populates against a real value on the original record always
// reads as "changed". Restrict the comparison to fields the platform's CSV
// actually maps in convertCSVHeaders.
const getFieldsToCompare = (platformId) => {
    if (
        platformId === '46140cdd-5134-4088-b474-9aad13987c9f' || // Zoom
        platformId === '86394d2a-acdd-4923-8623-dcae7541e641' // 8x8
    ) {
        return [
            'telephoneNumber',
            'managedBy',
            'attribute1',
            'attribute2',
            'attribute3',
        ];
    }
    const platformName = Cookies.get('selectedPlatformName');
    if (
        platformName === 'ms_resource_account' ||
        platformName === 'ms_resource_account_gcch'
    ) {
        return [
            'telephoneNumber',
            'voicePolicy',
            'managedBy',
            'attribute1',
            'attribute2',
            'attribute3',
        ];
    }
    return FIELDS_TO_COMPARE;
};
const FIELDS_SHOULD_NOT_CHANGE_MANAGED_BY_OTHERS = [
    //these fields shouldnt change if you are managed by others, so can only change : attribute1, attribute2, attribute 3, managedBy
    'telephoneNumber',
    'plan',
    'dialPlan',
    'voicePolicy',
];
const FIELDS_TO_COMPARE_UNLICENSED_USERS = [
    //these ARE THE ONLY FIELDS THAT CAN CHANGE if you are unlicensed
    'attribute1',
    'attribute2',
    'attribute3',
    'managedBy',
];
const FIELDS_NON_ATTRIBUTES = [
    'telephoneNumber',
    'dialPlan',
    'voicePolicy',
    'managedBy',
    'plan',
];
const LICENSE_NAME_INVALID_ERROR =
    'One or more license names are invalid. Please check the available licenses.';
const LICENSE_CHILD_WITHOUT_PARENT_ERROR =
    'Cannot assign a child license without its parent license.';
const LICENSE_CAPACITY_ERROR =
    'One or more licenses have no available capacity.';

const DEFAULT_COMPANY_DATA = {
    availableUsers: [],
    allNumbers: [],
    availableNumbers: [],
    allNumbersLookup: {},
    availableDPVPByService: {},
    availableNumbersLookup: {},
    availableDialPlansLookup: {},
    availablePlansLookup: {},
    availableVoicePoliciesLookup: {},
    availableUsersLookup: {},
    availableLicensesByName: {},
    availableLicensesById: {},
    licenseUsage: [],
};
const makeDateStr = () => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${yy}${mm}${dd}_${hh}${min}${ss}`;
};

const slugify = (str) =>
    (str || '').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');

// Builds a filename that reflects the currently selected platform, e.g.
// Acme_MicrosoftTeamsUsers_Modified_Validation_260806_120000.csv
const buildUsersFilename = (companyName, platformName, type, phase) => {
    const companySlug = slugify(companyName) || 'Company';
    const platformSlug = slugify(platformName) || 'Users';
    return `${companySlug}_${platformSlug}_${type}_${phase}_${makeDateStr()}.csv`;
};

// Converts {title, field} column defs (as used by the on-screen grid) into
// a field->title export map, so downloads always match what's on screen.
const columnsToFieldsMap = (columns) =>
    columns
        .filter((c) => c.field !== 'verificationStatus')
        .reduce((acc, c) => {
            acc[c.field] = c.title;
            return acc;
        }, {});

export const DEFAULT_VERIFICATION_RESULT = {
    passed: {
        columns: [],
        data: [],
    },
    failed: {
        columns: [],
        data: [],
    },
};
/**
 *CSV Hook to handle validating CSV data from user's uploads.
 * goto processCSVData for the flow of the hook.
 */
export default function useCSVUsers() {
    const [verificationChecks, setVerificationChecks] =
        React.useState(VERIFICATION_CHECKS);
    const [uploadData, setUploadData] = React.useState(null);
    const [dataJSON, setDataJSON] = React.useState(null);
    const [companyData, setCompanyData] = React.useState(DEFAULT_COMPANY_DATA);
    const [payloads, setPayloads] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [csvLimit, setCSVLimit] = React.useState(false);
    const [verificationResult, setVerificationResult] = React.useState(
        DEFAULT_VERIFICATION_RESULT,
    );
    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { data: currentCompanyData } = useGetCurrentCompany();
    const { requiredScope } = useSelector((state) => state.login);
    /**
     * for UI Validation progress checks, sets the next index to be in progress.
     * @param {*} i index in VERIFICATION_CHECKS that needs to be updated.
     */
    const changeVerificationChecks = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const newVerificationChecks = [...prev];
                    // Safety check: ensure index exists
                    if (newVerificationChecks[index]) {
                        newVerificationChecks[index].check = true;
                        if (index < newVerificationChecks.length - 1) {
                            newVerificationChecks[index + 1].current = true;
                        }
                    }
                    return newVerificationChecks;
                });
            },
            750,
            i,
        );
    };

    /**
     * for UI Validation progress checks, increments current index's completionCounter.
     * @param {*} i index in VERIFICATION_CHECKS that needs to be updated.
     */
    const changeVerificationProgresses = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const newVerificationChecks = [...prev];
                    // Safety check: ensure index exists
                    if (newVerificationChecks[index]) {
                        newVerificationChecks[index].completionCounter += 1;
                    }
                    return newVerificationChecks;
                });
            },
            750,
            i,
        );
    };
    //file change handler to Upload CSV
    const handleUploadUsersData = async (e) => {
        const platformId = getPlatformId();
        const platformName = Cookies.get('selectedPlatformName');
        console.log(platformName, platformId);
        const isRingCentral = platformName === 'RingCentral';
        let verf_checks = [];
        if (
            platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
            platformId === '86394d2a-acdd-4923-8623-dcae7541e641'
        ) {
            verf_checks = [
                {
                    label: 'Received CSV',
                    check: false,
                    current: true,
                    completionCounter: 0,
                    total: 1,
                },
                {
                    label: 'Fetching Company Data',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 4,
                },
                {
                    label: 'Converting CSV',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 3,
                },
                {
                    label: 'Verifying Users List and Status',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 5,
                },
                {
                    label: 'Verifying Numbers List and Status',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 5,
                },
                {
                    label: 'Verifying Licenses',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 3,
                },
            ];
        } else if (
            platformName === 'ms_resource_account' ||
            platformName === 'ms_resource_account_gcch'
        ) {
            verf_checks = [
                {
                    label: 'Received CSV',
                    check: false,
                    current: true,
                    completionCounter: 0,
                    total: 1,
                },
                {
                    label: 'Fetching Company Data',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 4,
                },
                {
                    label: 'Converting CSV',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 3,
                },
                {
                    label: 'Verifying Users List and Status',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 5,
                },
                {
                    label: 'Verifying Numbers List and Status',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 5,
                },
                {
                    label: 'Verifying Voice Policy List',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 2,
                },
                // {
                //     label: 'Verifying Dial Plan List',
                //     check: false,
                //     current: false,
                //     completionCounter: 0,
                //     total: 2,
                // },
            ];
        } else if (isRingCentral) {
            verf_checks = [
                {
                    label: 'Received CSV',
                    check: false,
                    current: true,
                    completionCounter: 0,
                    total: 1,
                },
                {
                    label: 'Fetching Company Data',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 4,
                },
                {
                    label: 'Converting CSV',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 3,
                },
                {
                    label: 'Verifying Users List and Status',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 5,
                },
                {
                    label: 'Verifying Numbers List and Status',
                    check: false,
                    current: false,
                    completionCounter: 0,
                    total: 5,
                },
            ];
        } else {
            verf_checks = VERIFICATION_CHECKS;
        }
        setVerificationChecks(verf_checks);
        if (
            e.target.files[0].type === CSV_TYPE ||
            e.target.files[0].type === CSV_TYPE_FX
        ) {
            const data = e.target.files[0];

            const { data: newDataJSON } = await convertCSVDataToJSON(data);

            // check for csv limits
            if (newDataJSON.length > appConfig.csvLimit) {
                setCSVLimit(true);
                handleReset();
                return;
            }

            if (csvLimit) setCSVLimit(false);

            //save data
            setLoading(true);
            setUploadData(newDataJSON);
            changeVerificationProgresses(0);
            changeVerificationChecks(0);

            //fetch data
            fetchCompanyData();
        } else {
            window.alert(CSV_TYPE_ERROR);
        }
    };
    //reset the whole UI states
    const handleReset = () => {
        setUploadData(null);
        setDataJSON(null);
        setCompanyData(DEFAULT_COMPANY_DATA);
        setVerificationResult(DEFAULT_VERIFICATION_RESULT);
        setPayloads([]);
        setLoading(false);
        setVerificationChecks((prev) => {
            const newVerificationChecks = [...prev];
            newVerificationChecks.forEach((v, index) => {
                v.check = false;
                v.current = index === 0; // NOTE: restart at Received CSV check
                v.completionCounter = 0;
            });
            return newVerificationChecks;
        });
    };
    //fetch relevant company's data for validations
    //fetch relevant company's data for validations
    const fetchCompanyData = async () => {
        try {
            // Add try...catch for error handling
            const platformId = getPlatformId();
            const selectedPlatformName = Cookies.get('selectedPlatformName');
            const isResourceAccount =
                selectedPlatformName === 'ms_resource_account' ||
                selectedPlatformName === 'ms_resource_account_gcch';
            const isRingCentralPlatform =
                selectedPlatformName === 'RingCentral';
            const is8x8Platform =
                platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
                platformId === '86394d2a-acdd-4923-8623-dcae7541e641';

            // Fetch users, available numbers, all numbers, plans, and services concurrently
            const [
                availableUsersResponse,
                allNumbersResponse,
                availableNumbersResponse,
                plansResponse,
                availableServicesResponses,
                allNums,
                // 8x8-only: a second, parallel fetch of PlatformUsers with no
                // `usertype` param at all. The `usertype` query param on this
                // endpoint only supports 'user' / 'resourceaccount' per the
                // API's Swagger doc — 'ringgroup' is not a valid filter value —
                // but omitting the param entirely returns every user type, so
                // this is how existing 8x8 Ring Groups get included below.
                allUserTypesResponse,
            ] = await Promise.all([
                axios.get(
                    `/PlatformUsers/${currentCompany}/Platform/${platformId}`,
                    {
                        params: {
                            usertype: isResourceAccount
                                ? 'resourceaccount'
                                : 'user',
                        },
                    },
                ),
                axios.get(
                    `/company/${currentCompany}/PlatformNumbers/${platformId}`,
                ),
                axios.get(
                    `/company/${currentCompany}/Platform/${platformId}/Numbers/Available`,
                ),
                currentCompanyData?.companyBillingSettings?.perUserPlan != 0
                    ? axios.get(`/company/${currentCompany}/plans`)
                    : Promise.resolve({ data: [] }),
                // Only fetch services if not resource account
                isResourceAccount
                    ? axios.get(`/Services/Company/${currentCompany}`)
                    : axios.get(`/Services/Company/${currentCompany}`),
                axios.get(
                    `/company/${currentCompany}/Platform/${platformId}/Numbers/Available/dropdown`,
                ),
                is8x8Platform
                    ? axios.get(
                          `/PlatformUsers/${currentCompany}/Platform/${platformId}`,
                      )
                    : Promise.resolve(null),
            ]);
            changeVerificationProgresses(1);

            // 8x8-only: merge existing Ring Groups (userType === 'RingGroup')
            // into the platformUsers list so they become bulk-editable
            // (Number + Licences) via this CSV Update flow, the same as a
            // regular 8x8 User. They're forced to isLicensed: true here —
            // RingGroupEditContent.jsx (the drawer for editing a single Ring
            // Group) has no license-gating concept at all and always allows
            // Number/Licence edits, so this mirrors that behavior and avoids
            // the unlicensed-users gate below silently dropping Number/Licence
            // changes for a Ring Group CSV row instead of processing them.
            if (is8x8Platform && allUserTypesResponse) {
                const existingIds = new Set(
                    availableUsersResponse.data.platformUsers.map((u) => u.id),
                );
                const ringGroupUsers = (
                    allUserTypesResponse.data?.platformUsers || []
                )
                    .filter(
                        (u) =>
                            u.userType === 'RingGroup' &&
                            !existingIds.has(u.id),
                    )
                    .map((u) => ({ ...u, isLicensed: true }));
                availableUsersResponse.data.platformUsers = [
                    ...availableUsersResponse.data.platformUsers,
                    ...ringGroupUsers,
                ];
            }

            // --- 8x8 License Data ---
            let availableLicensesByName = {};
            let availableLicensesById = {};
            let licenseUsage = [];

            if (
                platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
                platformId === '86394d2a-acdd-4923-8623-dcae7541e641'
            ) {
                const eightByEightService =
                    availableServicesResponses.data.find(
                        (s) => s.serviceType === 'EightByEight',
                    );
                const [availableLicensesResponse, licenseUsageResponse] =
                    await Promise.all([
                        axios.get(
                            `/Services/EightByEight/Company/${currentCompany}/Licenses/Available`,
                        ),
                        eightByEightService
                            ? axios.get(
                                  `/Service/EightByEight/${eightByEightService.id}/Licenses`,
                              )
                            : Promise.resolve({ data: [] }),
                    ]);
                availableLicensesByName = keyBy(
                    availableLicensesResponse.data,
                    'name',
                );
                availableLicensesById = keyBy(
                    availableLicensesResponse.data,
                    'id',
                );
                licenseUsage = licenseUsageResponse.data;
            }

            // --- DP/VP Lookup Creation (Skip for resource accounts) ---
            let availableDialPlansLookup = {};
            let availableVoicePoliciesLookup = {};
            let availableDPVPByService = {};
            // Separate ID-keyed lookups, needed to resolve a user's
            // msDialPlanID/msVoicePolicyID (GUIDs) to a display name — the
            // lookups above are keyed by name (used elsewhere to validate a
            // CSV-provided name actually exists), so looking them up by ID
            // always misses and leaves dialPlan/voicePolicy undefined.
            let dialPlansByIdLookup = {};
            let voicePoliciesByIdLookup = {};

            if (
                platformId !== '46140cdd-5134-4088-b474-9aad13987c9f' &&
                platformId !== '86394d2a-acdd-4923-8623-dcae7541e641' &&
                !isRingCentralPlatform
            ) {
                const individualServicesResponses = [];
                availableServicesResponses.data.forEach(async (v) => {
                    if (
                        v.serviceType === DR_SERVICE_TYPE ||
                        v.serviceType === DR_GCCH_SERVICE_TYPE
                    ) {
                        individualServicesResponses.push(
                            axios.get(`/service/${v.id}`),
                        );
                    }
                });
                const servicesResponses = await Promise.all(
                    individualServicesResponses,
                );

                const allDRVPandDP = servicesResponses.map((v) => {
                    const voicePolicies = keyBy(v.data.voicePolicies, 'name');
                    const dialPlans = keyBy(v.data.dialPlans, 'name');
                    availableVoicePoliciesLookup = {
                        ...availableVoicePoliciesLookup,
                        ...voicePolicies,
                    };
                    availableDialPlansLookup = {
                        ...availableDialPlansLookup,
                        ...dialPlans,
                    };
                    voicePoliciesByIdLookup = {
                        ...voicePoliciesByIdLookup,
                        ...keyBy(v.data.voicePolicies, 'id'),
                    };
                    dialPlansByIdLookup = {
                        ...dialPlansByIdLookup,
                        ...keyBy(v.data.dialPlans, 'id'),
                    };
                    return {
                        id: v.data.id,
                        serviceType: v.data.serviceType,
                        voicePolicies,
                        dialPlans,
                    };
                });
                availableDPVPByService = keyBy(allDRVPandDP, 'id');
            }
            changeVerificationProgresses(1);
            // --- Number Lookups ---
            // Create lookup for ALL numbers (ID -> telephoneNumber string)
            const allNumbersIdLookup =
                allNumbersResponse.data.platformNumbers.reduce(
                    (acc, number) => {
                        // Ensure numberID exists and is not null/undefined before adding
                        if (number.numberID != null) {
                            acc[number.numberID] = number.telephoneNumber;
                        }
                        return acc;
                    },
                    {},
                );

            // Create lookup for AVAILABLE numbers (telephoneNumber string -> full number object)
            const availableNumbersLookup = keyBy(
                allNums.data,
                'telephoneNumber',
            );
            // Create lookup for ALL numbers (telephoneNumber string -> full number object) - used for DR check
            const allNumbersTelLookup = keyBy(
                allNumbersResponse.data.platformNumbers,
                'telephoneNumber',
            );

            changeVerificationProgresses(1); // Increment progress

            // --- User Data Transformation ---
            // Transform the raw user data similar to the example provided

            const transformedUsersData =
                availableUsersResponse.data.platformUsers.map((user) => {
                    const userProfile = user.userProfiles?.[0];
                    let telephoneNumber = '';
                    // Skip dial plan and voice policy for resource accounts.
                    // Resolve by ID first, then take the name — the
                    // name-keyed lookups above are for a different purpose
                    // (validating a CSV-provided name exists).
                    let dialPlan = isResourceAccount
                        ? ''
                        : userProfile?.msDialPlanID
                          ? dialPlansByIdLookup[userProfile.msDialPlanID]
                                ?.name || ''
                          : '';
                    let voicePolicy = userProfile?.msVoicePolicyID
                        ? voicePoliciesByIdLookup[userProfile.msVoicePolicyID]
                              ?.name || ''
                        : '';

                    if (!user.isLicensed) {
                        telephoneNumber = '';
                    } else if (user.isLicensed && user.msCallingPlanEnabled) {
                        telephoneNumber = 'MS Calling Plan Assigned';
                    } else if (
                        userProfile?.numberID &&
                        allNumbersIdLookup[userProfile.numberID]
                    ) {
                        telephoneNumber =
                            userProfile?.platformNumber?.telephoneNumber || '';
                    }
                    // The raw platform-user record carries these as
                    // platformAttribute1/2/3, not attribute1/2/3 — without
                    // this rename they're absent from the lookup entirely,
                    // making any user with real attribute values always
                    // compare as changed.
                    const attributes = {
                        attribute1: user.platformAttribute1,
                        attribute2: user.platformAttribute2,
                        attribute3: user.platformAttribute3,
                    };

                    if (
                        platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
                        platformId === '86394d2a-acdd-4923-8623-dcae7541e641' ||
                        isRingCentralPlatform
                    ) {
                        return {
                            ...user,
                            ...attributes,
                            telephoneNumber,
                        };
                    } else if (isResourceAccount) {
                        return {
                            ...user,
                            ...attributes,
                            telephoneNumber,
                            voicePolicy,
                        };
                    } else {
                        return {
                            ...user,
                            ...attributes,
                            telephoneNumber,
                            dialPlan,
                            voicePolicy,
                        };
                    }
                });

            // Create the final lookup using the TRANSFORMED user data
            const availableUsersLookup = keyBy(
                transformedUsersData,
                'userPrincipalName',
            );

            changeVerificationProgresses(1); // Increment progress

            // --- Plan Lookup ---
            const availablePlansLookup = keyBy(plansResponse.data, 'name');
            changeVerificationProgresses(1); // Increment progress for plans step

            // Set the company data state with the transformed/correctly structured lookups
            setCompanyData({
                availableUsers: availableUsersResponse.data.platformUsers,
                availableNumbers: availableNumbersResponse.data,
                allNumbers: allNumbersResponse.data.platformNumbers,
                availableDPVPByService,
                availableUsersLookup: keyBy(
                    transformedUsersData,
                    'userPrincipalName',
                ),
                availableNumbersLookup: availableNumbersLookup,
                availablePlansLookup,
                allNumbersLookup: allNumbersTelLookup,
                availableDialPlansLookup: isResourceAccount
                    ? {}
                    : availableDialPlansLookup,
                availableVoicePoliciesLookup: availableVoicePoliciesLookup,
                availableLicensesByName,
                availableLicensesById,
                licenseUsage,
            });

            changeVerificationChecks(1); // Mark Fetching Company Data as complete
        } catch (err) {
            console.error('Error fetching company data:', err);
            setError(
                'Failed to fetch necessary company data. Please try again.',
            );
            // Optionally reset state or handle error appropriately
            handleReset(); // Reset if fetch fails critically
        }
    };

    const usersUnlicensedFilter = (v) =>
        !companyData.availableUsersLookup[v.userPrincipalName]?.isLicensed;
    const usersLicensedFilter = (v) => {
        return companyData.availableUsersLookup[v.userPrincipalName]
            ?.isLicensed;
    };

    const usersNotOKFilter = (v) => false;
    const numbersNotOKFilter = (v) => false;
    const numbersNeedAssignmentFilter = (v) =>
        v.telephoneNumber?.length > 0 && usersLicensedFilter(v);
    const stringNotEmptyFilter = (v) => v && v.length > 0;
    const noErrorsFilter = (v) => v.errors.length < 1;
    const haveErrorsFilter = (v) => v.errors.length > 0;
    // A user can be on the default plan represented as null/undefined on the
    // API record, an empty CSV cell, or the literal "Company Default Plan"
    // text the template sometimes writes (see useCSVTemplate.js) — all three
    // mean the same thing for comparison purposes.
    const normalizePlanValue = (value) =>
        value == null || value === '' || value === 'Company Default Plan'
            ? ''
            : value;
    // CSV cells always parse to '' when blank — they can never be null,
    // undefined, or simply absent. The original API record, however, either
    // stores an unset optional field as null, OR omits the key entirely
    // (plan is frequently just never present at all for a default-plan
    // user). Comparing directly would flag every such user as changed — not
    // just because the value differs, but because lodash.isEqual treats two
    // objects with different key sets as unequal even when the "extra" key's
    // value is empty. So both sides are normalized over the union of keys
    // either one carries, not just each object's own keys independently.
    const normalizeValueForCompare = (key, value) =>
        key === 'plan'
            ? normalizePlanValue(value)
            : value === null || value === undefined
              ? ''
              : value;

    const dataIsUnchangedFilter = (originalData, uploadedData) => {
        const allKeys = new Set([
            ...Object.keys(originalData || {}),
            ...Object.keys(uploadedData || {}),
        ]);
        const normalize = (obj) =>
            Array.from(allKeys).reduce((acc, key) => {
                acc[key] = normalizeValueForCompare(key, (obj || {})[key]);
                return acc;
            }, {});
        return isEqual(normalize(originalData), normalize(uploadedData));
    };
    const callValuesConsistentFilter = (v) =>
        (v.telephoneNumber && v.dialPlan && v.voicePolicy) ||
        (!v.telephoneNumber && !v.dialPlan && !v.voicePolicy);
    const callValueConsistentFilter = (v) =>
        (v.telephoneNumber && v.voicePolicy) ||
        (!v.telephoneNumber && !v.voicePolicy);
    const licensedUserIsChangedFilter = (user) => {
        const fieldsToCompare = getFieldsToCompare(getPlatformId());
        const originalUser =
            companyData.availableUsersLookup[user.userPrincipalName] || {};
        // 'plan' equivalence (null/undefined/'' vs the literal "Company
        // Default Plan" text) is handled generically by normalizeForCompare
        // inside dataIsUnchangedFilter — no per-user sentinel needed here.
        const userOriginalData = pick(originalUser, fieldsToCompare);
        const userUploadedData = pick(user, fieldsToCompare);
        return !dataIsUnchangedFilter(userOriginalData, userUploadedData);
    };
    const unlicensedUserIsChangedFilter = (user) => {
        const userOriginalData = pick(
            companyData.availableUsersLookup[user.userPrincipalName] || {},
            FIELDS_TO_COMPARE_UNLICENSED_USERS,
        );
        const userUploadedData = pick(user, FIELDS_TO_COMPARE_UNLICENSED_USERS);
        return !dataIsUnchangedFilter(userOriginalData, userUploadedData);
    };
    const upnIsNotEmptyFilter = (user) => user.userPrincipalName;

    const upnIsValidFilter = (user) =>
        Boolean(companyData.availableUsersLookup[user.userPrincipalName]);
    /**
     * filter JSON data to cut down number of checks needed.
     * finds all licensed users that is changed in CSV.
     * @param {*} data CSV data converted into JSON
     * @returns all licensed users that has changes
     */
    const filterDataJSON = (data) => {
        return data.filter(
            (v) =>
                upnIsValidFilter(v) &&
                usersLicensedFilter(v) &&
                licensedUserIsChangedFilter(v),
        );
    };
    //convert CSV columns into friendlier backend's JSON field
    const convertCSVHeaders = (data) => {
        const platformId = getPlatformId();
        if (
            platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
            platformId === '86394d2a-acdd-4923-8623-dcae7541e641'
        ) {
            return data
                .map((v) => {
                    const rawTelephoneNumber = v['Telephone Number'];
                    const transformedObject = {
                        ...DEFAULT_JSON_DATA,
                        userPrincipalName: v['UPN'],
                        displayName: v['Display Name'],
                        // Informational only — sourced straight from the CSV
                        // cell, not validated or used to change account type.
                        accountType: v['Account Type'],
                        active: v['Active'],
                        telephoneNumber:
                            rawTelephoneNumber &&
                            rawTelephoneNumber !== 'Not Allocated' &&
                            rawTelephoneNumber !== 'Not Licensed'
                                ? String(rawTelephoneNumber)
                                : '',
                        licenses: v['Licenses']
                            ? v['Licenses']
                                  .split(';')
                                  .map((s) => s.trim())
                                  .filter(Boolean)
                            : [],
                        managedBy:
                            v['Managed By (TCAP/Other)'] &&
                            v['Managed By (TCAP/Other)'].length > 0
                                ? v['Managed By (TCAP/Other)'].toUpperCase() ===
                                  'TCAP'
                                    ? 0
                                    : 1
                                : undefined,
                        attribute1: v['Attribute 1'],
                        attribute2: v['Attribute 2'],
                        attribute3: v['Attribute 3'],
                    };

                    const originalData =
                        companyData.availableUsersLookup[
                            transformedObject.userPrincipalName
                        ];
                    console.log(originalData);
                    transformedObject.isTelephoneNumberChanged =
                        originalData &&
                        !isEqual(
                            originalData.telephoneNumber,
                            transformedObject.telephoneNumber,
                        );

                    const userOriginalData = pick(originalData, [
                        'telephoneNumber',
                        'managedBy',
                    ]);
                    const userUploadedData = pick(transformedObject, [
                        'telephoneNumber',
                        'managedBy',
                    ]);

                    const licensedUsersAttributesUpdatesOnly =
                        originalData?.isLicensed &&
                        dataIsUnchangedFilter(
                            userOriginalData,
                            userUploadedData,
                        );
                    const changeManagedByToTCAP =
                        transformedObject.managedBy === 0 &&
                        originalData?.managedBy === 1;

                    // Remove conditions - accept all users including unlicensed/unallocated
                    transformedObject.attributesUpdateOnly = false;

                    return transformedObject;
                })
                .filter(upnIsNotEmptyFilter);
        } else if (
            Cookies.get('selectedPlatformName') === 'ms_resource_account' ||
            Cookies.get('selectedPlatformName') === 'ms_resource_account_gcch'
        ) {
            return data
                .map((v) => {
                    const rawTelephoneNumber = v['Telephone Number'];
                    const transformedObject = {
                        ...DEFAULT_JSON_DATA,
                        userPrincipalName: v['UPN'],
                        displayName: v['Display Name'],
                        active: v['Active'],
                        telephoneNumber:
                            rawTelephoneNumber &&
                            rawTelephoneNumber !== 'Not Allocated' &&
                            rawTelephoneNumber !== 'Not Licensed'
                                ? String(rawTelephoneNumber)
                                : '',
                        voicePolicy: v['Voice Policy'],
                        usageLocation: v['Usage Location'],
                        plan: v['Plan'],
                        managedBy:
                            v['Managed By (TCAP/Other)'] &&
                            v['Managed By (TCAP/Other)'].length > 0
                                ? v['Managed By (TCAP/Other)'].toUpperCase() ===
                                  'TCAP'
                                    ? 0
                                    : 1
                                : undefined,
                        attribute1: v['Attribute 1'],
                        attribute2: v['Attribute 2'],
                        attribute3: v['Attribute 3'],
                    };

                    const originalData =
                        companyData.availableUsersLookup[
                            transformedObject.userPrincipalName
                        ];
                    console.log(originalData);
                    transformedObject.isTelephoneNumberChanged =
                        originalData &&
                        !isEqual(
                            originalData.telephoneNumber,
                            transformedObject.telephoneNumber,
                        );
                    const isNewTelephoneNumberUsingDR =
                        transformedObject.telephoneNumber &&
                        companyData.availableNumbersLookup[
                            transformedObject.telephoneNumber
                        ]?.trunkType === DR_TRUNK_TYPE;
                    const isOldTelephoneNumberUsingDR =
                        originalData &&
                        originalData.telephoneNumber &&
                        companyData.allNumbersLookup[
                            originalData.telephoneNumber
                        ]?.trunkType === DR_TRUNK_TYPE;
                    transformedObject.isDRNumber =
                        transformedObject.isTelephoneNumberChanged
                            ? isNewTelephoneNumberUsingDR
                            : isOldTelephoneNumberUsingDR;
                    console.log(
                        companyData.availableVoicePoliciesLookup[
                            transformedObject.voicePolicy
                        ],
                    );
                    const isVPChanged =
                        stringNotEmptyFilter(transformedObject.voicePolicy) &&
                        !isEqual(
                            companyData.availableVoicePoliciesLookup[
                                transformedObject.voicePolicy
                            ]?.name,
                            transformedObject.voicePolicy,
                        );
                    // const isDPChanged =
                    //     stringNotEmptyFilter(transformedObject.dialPlan) &&
                    //     !isEqual(
                    //         originalData?.dialPlan,
                    //         transformedObject.dialPlan,
                    //     );
                    transformedObject.isDPOrVPChanged =
                        transformedObject.isDRNumber && isVPChanged;

                    // check if a plan has been changed, else ignore
                    let isPlanChanged = false;
                    if (
                        currentCompanyData?.companyBillingSettings
                            ?.perUserPlan != 0
                    ) {
                        // The template doesn't always write literal "Company
                        // Default Plan" text for a default-plan user (see
                        // useCSVTemplate.js) — normalize both sides the same
                        // way rather than assuming one canonical form.
                        isPlanChanged =
                            normalizePlanValue(originalData?.plan) !==
                            normalizePlanValue(transformedObject.plan);
                    }
                    transformedObject.isPlanChanged = isPlanChanged;

                    const userOriginalData = pick(
                        originalData,
                        FIELDS_NON_ATTRIBUTES,
                    );
                    const userUploadedData = pick(
                        transformedObject,
                        FIELDS_NON_ATTRIBUTES,
                    );

                    const licensedUsersAttributesUpdatesOnly =
                        originalData?.isLicensed &&
                        dataIsUnchangedFilter(
                            userOriginalData,
                            userUploadedData,
                        );
                    const changeManagedByToTCAP =
                        transformedObject.managedBy === 0 &&
                        originalData?.managedBy === 1;

                    transformedObject.attributesUpdateOnly =
                        licensedUsersAttributesUpdatesOnly ||
                        (originalData &&
                            originalData?.managedBy !== 0 &&
                            !changeManagedByToTCAP);

                    return transformedObject;
                })
                .filter(upnIsNotEmptyFilter);
        } else {
            return data
                .map((v) => {
                    const rawTelephoneNumber = v['Telephone Number'];
                    const transformedObject = {
                        ...DEFAULT_JSON_DATA,
                        userPrincipalName: v['UPN'],
                        displayName: v['Display Name'],
                        active: v['Active'],
                        telephoneNumber:
                            rawTelephoneNumber &&
                            rawTelephoneNumber !== 'Not Allocated' &&
                            rawTelephoneNumber !== 'Not Licensed'
                                ? String(rawTelephoneNumber)
                                : '',
                        dialPlan: v['Dial Plan'],
                        voicePolicy: v['Voice Policy'],
                        usageLocation: v['Usage Location'],
                        plan: v['Plan'],
                        managedBy:
                            v['Managed By (TCAP/Other)'] &&
                            v['Managed By (TCAP/Other)'].length > 0
                                ? v['Managed By (TCAP/Other)'].toUpperCase() ===
                                  'TCAP'
                                    ? 0
                                    : 1
                                : undefined,
                        attribute1: v['Attribute 1'],
                        attribute2: v['Attribute 2'],
                        attribute3: v['Attribute 3'],
                    };

                    const originalData =
                        companyData.availableUsersLookup[
                            transformedObject.userPrincipalName
                        ];
                    console.log(originalData);
                    transformedObject.isTelephoneNumberChanged =
                        originalData &&
                        !isEqual(
                            originalData.telephoneNumber,
                            transformedObject.telephoneNumber,
                        );
                    const isNewTelephoneNumberUsingDR =
                        transformedObject.telephoneNumber &&
                        companyData.availableNumbersLookup[
                            transformedObject.telephoneNumber
                        ]?.trunkType === DR_TRUNK_TYPE;
                    const isOldTelephoneNumberUsingDR =
                        originalData &&
                        originalData.telephoneNumber &&
                        companyData.allNumbersLookup[
                            originalData.telephoneNumber
                        ]?.trunkType === DR_TRUNK_TYPE;
                    transformedObject.isDRNumber =
                        transformedObject.isTelephoneNumberChanged
                            ? isNewTelephoneNumberUsingDR
                            : isOldTelephoneNumberUsingDR;
                    const isVPChanged =
                        stringNotEmptyFilter(transformedObject.voicePolicy) &&
                        !isEqual(
                            companyData.availableVoicePoliciesLookup[
                                transformedObject.voicePolicy
                            ]?.name,
                            transformedObject.voicePolicy,
                        );
                    console.log(companyData.availableDialPlansLookup);
                    const isDPChanged =
                        stringNotEmptyFilter(transformedObject.dialPlan) &&
                        !isEqual(
                            companyData.availableDialPlansLookup[
                                transformedObject.dialPlan
                            ]?.name,
                            transformedObject.dialPlan,
                        );
                    transformedObject.isDPOrVPChanged =
                        transformedObject.isDRNumber &&
                        (isDPChanged || isVPChanged);

                    // check if a plan has been changed, else ignore
                    let isPlanChanged = false;
                    if (
                        currentCompanyData?.companyBillingSettings
                            ?.perUserPlan != 0
                    ) {
                        // The template doesn't always write literal "Company
                        // Default Plan" text for a default-plan user (see
                        // useCSVTemplate.js) — normalize both sides the same
                        // way rather than assuming one canonical form.
                        isPlanChanged =
                            normalizePlanValue(originalData?.plan) !==
                            normalizePlanValue(transformedObject.plan);
                    }
                    transformedObject.isPlanChanged = isPlanChanged;

                    const userOriginalData = pick(
                        originalData,
                        FIELDS_NON_ATTRIBUTES,
                    );
                    const userUploadedData = pick(
                        transformedObject,
                        FIELDS_NON_ATTRIBUTES,
                    );

                    const licensedUsersAttributesUpdatesOnly =
                        originalData?.isLicensed &&
                        dataIsUnchangedFilter(
                            userOriginalData,
                            userUploadedData,
                        );
                    const changeManagedByToTCAP =
                        transformedObject.managedBy === 0 &&
                        originalData?.managedBy === 1;

                    transformedObject.attributesUpdateOnly =
                        licensedUsersAttributesUpdatesOnly ||
                        (originalData &&
                            originalData?.managedBy !== 0 &&
                            !changeManagedByToTCAP);

                    return transformedObject;
                })
                .filter(upnIsNotEmptyFilter);
        }
    };

    /**
     * after uploading data, process CSV into JSON & filter them.
     * @param {*} dataCSV CSV uploaded by user.
     */
    const processCSVData = async (dataCSV) => {
        changeVerificationProgresses(2);
        //convert CSV columns into friendlier backend's JSON field
        const convertedDataJSON = convertCSVHeaders(dataCSV);
        changeVerificationProgresses(2);

        //filter JSON data to cut down number of checks needed
        const filteredDataJSON = filterDataJSON(convertedDataJSON);
        changeVerificationProgresses(2);
        setDataJSON(filteredDataJSON);
        changeVerificationProgresses(2);
        changeVerificationChecks(2);

        //case 1: no data to be checked
        if (filteredDataJSON.length < 1) {
            const {
                unlicensedUsersChanged,
                ignored,
                upnIsInvalid,
                unlicensedUsersInvalid,
            } = findUnlicensedUsersAndIgnoredUsers({
                data: [],
                originalDataJSON: convertedDataJSON,
            });

            //case 1a: no unlicensed users to be checked as well -> do nothing.
            if (unlicensedUsersChanged.length < 1) {
                changeVerificationChecks(3);
                changeVerificationChecks(4);
                changeVerificationChecks(5);
                changeVerificationChecks(6);
                setError(NO_DATA_FOUND_ERROR);
                return;
            }
            //case 1b: some unlicensed users are  changed, but there's no licensed users changed.

            //report this to UI state
            const { allSucceedData } = reportResult({
                data: [],
                unlicensedUsersChanged,
                ignored,
                upnIsInvalid,
                unlicensedUsersInvalid,
            });
            //transform JSON to payload for BE api calls
            transformDataToPayload({ data: allSucceedData });
            changeVerificationChecks(3);
            changeVerificationChecks(4);
            changeVerificationChecks(5);
            changeVerificationChecks(6);
            changeVerificationChecks(7);
            return;
        }
        //case 2: some licensed users are changed

        //validate licensed user's data
        const { dataWithErrors } = await verifyCSVData({
            data: filteredDataJSON,
        });

        const {
            unlicensedUsersChanged,
            ignored,
            upnIsInvalid,
            unlicensedUsersInvalid,
        } = findUnlicensedUsersAndIgnoredUsers({
            data: dataWithErrors,
            originalDataJSON: convertedDataJSON,
        });
        //report these to UI states
        const { allSucceedData } = reportResult({
            data: dataWithErrors,
            unlicensedUsersChanged,
            ignored,
            upnIsInvalid,
            unlicensedUsersInvalid,
        });
        //transform necessary data into payload for BE api calls
        transformDataToPayload({ data: allSucceedData });
    };

    React.useEffect(() => {
        //after fetching Company Data, start processing & validating CSV data.
        if (!isEqual(companyData, DEFAULT_COMPANY_DATA)) {
            processCSVData(uploadData);
        }
    }, [companyData]);

    /**
     * aggregates uploaded CSV user data with user's data from BE for API call payloads.
     * @param {*} param0
     */
    // const transformDataToPayload = async ({ data }) => {
    //     const {
    //         availableNumbersLookup,
    //         availableVoicePoliciesLookup,
    //         availableDialPlansLookup,
    //         availableUsersLookup,
    //         availablePlansLookup,
    //     } = companyData;

    //     const newPayload = data.filter(noErrorsFilter).map((v) => {
    //         const userAPIData =
    //             availableUsersLookup[v.userPrincipalName] || {};
    //         const userData = {
    //             ...userAPIData,
    //             ...v,
    //         };

    //         if (usersUnlicensedFilter(userData)) {
    //             return userData;
    //         }
    //         const numberID = v.isTelephoneNumberChanged
    //             ? (
    //                   numbersNeedAssignmentFilter(userData) &&
    //                   availableNumbersLookup[userData.telephoneNumber]
    //               )?.id
    //             : userAPIData.numberID;

    //         let planID = null;
    //         if (currentCompanyData?.companyBillingSettings?.perUserPlan != 0) {
    //             planID = isEmpty(v.plan)
    //                 ? v.plan
    //                 : v.plan === 'Company Default Plan'
    //                   ? null
    //                   : availablePlansLookup[v.plan]?.id;
    //         }
    //         return {
    //             ...userData,
    //             dialPlanID:
    //                 //TODO: extract original data if VP, DP , Number not cchanged

    //                 (
    //                     stringNotEmptyFilter(userData.dialPlan) &&
    //                     availableDialPlansLookup[userData.dialPlan]
    //                 )?.id,
    //             voicePolicyID: (
    //                 stringNotEmptyFilter(userData.voicePolicy) &&
    //                 availableVoicePoliciesLookup[userData.voicePolicy]
    //             )?.id,
    //             numberID,
    //             planID,
    //         };
    //     });
    //     console.log(newPayload)
    //     setPayloads(newPayload);
    // };
    const transformDataToPayload = async ({ data }) => {
        const {
            availableNumbersLookup,
            availableVoicePoliciesLookup,
            availableDialPlansLookup,
            availableUsersLookup,
        } = companyData;

        const platformId = getPlatformId();
        const newPayload = data.filter(noErrorsFilter).flatMap((v) => {
            const userAPIData = availableUsersLookup[v.userPrincipalName] || {};
            const availableNUmberId =
                availableNumbersLookup[v.telephoneNumber]?.numberID;
            const userData = { ...v };
            const payloads = [];
            console.log('userAPIData', userData);
            // Always add platformUserUpdate payload for all users
            payloads.push({
                type: 'platformUserUpdate',
                payload: {
                    id: userAPIData.id,
                    managedBy: userData.managedBy,
                    userType: userAPIData.userType,
                    platformAttribute1: userData.attribute1,
                    platformAttribute2: userData.attribute2,
                    platformAttribute3: userData.attribute3,
                },
            });

            // For 8x8: mirror the drawer's logic — delete if number cleared,
            // then POST/PUT UserProfile with licenses + numberID combined.
            const is8x8Platform =
                platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
                platformId === '86394d2a-acdd-4923-8623-dcae7541e641';

            if (is8x8Platform) {
                const existingNumberID =
                    userAPIData?.userProfiles?.[0]?.numberID;
                const numberCleared =
                    v.isTelephoneNumberChanged &&
                    v.telephoneNumber === '' &&
                    existingNumberID !== undefined;
                const numberAssigned =
                    v.isTelephoneNumberChanged &&
                    v.telephoneNumber !== '' &&
                    availableNUmberId !== existingNumberID;

                const currentLicenseNames = (
                    userAPIData?.userProfiles?.[0]?.licenses || []
                )
                    .map((l) => l.name)
                    .sort();
                const uploadedLicenseNames = [
                    ...(userData.licenses || []),
                ].sort();
                const licensesChanged = !isEqual(
                    currentLicenseNames,
                    uploadedLicenseNames,
                );

                // Step 1: DELETE profile if number was cleared (same as drawer Step 2)
                if (numberCleared) {
                    payloads.push({
                        type: 'deleteProfile',
                        queryParams: {
                            numberID: existingNumberID,
                            platformID: platformId,
                            platformUserID: userAPIData.id,
                            UPN: v.userPrincipalName,
                        },
                    });
                }

                // Step 2: POST/PUT UserProfile combining licenses + number
                // (same as drawer Step 3 — only when not just cleared)
                if ((licensesChanged || numberAssigned) && !numberCleared) {
                    const { availableLicensesByName } = companyData;
                    const licenseObjects = (userData.licenses || [])
                        .filter(Boolean)
                        .map((name) => {
                            const catalogItem = availableLicensesByName[name];
                            return {
                                id: catalogItem?.id || '',
                                name,
                                description: catalogItem?.description || null,
                            };
                        });

                    const resolvedNumberID = numberAssigned
                        ? availableNUmberId
                        : existingNumberID;

                    const hasExistingProfile =
                        (userAPIData?.userProfiles?.length ?? 0) > 0;

                    const profilePayload = {
                        platformUserID: userAPIData.id,
                        platformID: platformId,
                        licenses: licenseObjects,
                        UPN: v.userPrincipalName,
                    };

                    if (resolvedNumberID) {
                        profilePayload.numberID = resolvedNumberID;
                    }

                    payloads.push({
                        type: hasExistingProfile
                            ? 'update8x8Licenses'
                            : 'create8x8Licenses',
                        payload: profilePayload,
                    });
                }
            }

            if (
                !is8x8Platform &&
                v.isTelephoneNumberChanged &&
                userAPIData.userProfiles[0]?.numberID !== undefined &&
                (v.telephoneNumber === '' ||
                    userAPIData.userProfiles[0]?.numberID !== availableNUmberId)
            ) {
                payloads.push({
                    type: 'deleteProfile',
                    queryParams: {
                        numberID: userAPIData.userProfiles[0]?.numberID,
                        platformID: platformId,
                        platformUserID: userAPIData.id,
                        UPN: v.userPrincipalName,
                    },
                });
            }

            if (!is8x8Platform) {
                const existingNumberID = userAPIData.userProfiles[0]?.numberID;
                if (
                    v.isTelephoneNumberChanged &&
                    v.telephoneNumber !== '' &&
                    existingNumberID !== availableNUmberId
                ) {
                    payloads.push({
                        type: 'createProfile',
                        payload: {
                            numberID: availableNUmberId,
                            platformID: platformId,
                            platformUserID: userAPIData.id,
                            msDialPlanID:
                                Cookies.get('selectedPlatformName') !==
                                    'ms_resource_account' &&
                                Cookies.get('selectedPlatformName') !==
                                    'ms_resource_account_gcch'
                                    ? (
                                          stringNotEmptyFilter(
                                              userData?.dialPlan,
                                          ) &&
                                          availableDialPlansLookup[
                                              userData.dialPlan
                                          ]
                                      )?.id
                                    : undefined,
                            msVoicePolicyID: (
                                stringNotEmptyFilter(userData?.voicePolicy) &&
                                availableVoicePoliciesLookup[
                                    userData.voicePolicy
                                ]
                            )?.id,
                            UPN: v.userPrincipalName,
                        },
                    });
                } else if (v.voicePolicy !== '' || v.dialPlan !== '') {
                    payloads.push({
                        type: 'updateProfile',
                        payload: {
                            numberID:
                                existingNumberID === undefined
                                    ? availableNUmberId
                                    : existingNumberID,
                            platformID: platformId,
                            platformUserID: userAPIData.id,
                            msDialPlanID:
                                Cookies.get('selectedPlatformName') !==
                                    'ms_resource_account' &&
                                Cookies.get('selectedPlatformName') !==
                                    'ms_resource_account_gcch'
                                    ? (
                                          stringNotEmptyFilter(
                                              userData?.dialPlan,
                                          ) &&
                                          availableDialPlansLookup[
                                              userData.dialPlan
                                          ]
                                      )?.id
                                    : undefined,
                            msVoicePolicyID: (
                                stringNotEmptyFilter(userData?.voicePolicy) &&
                                availableVoicePoliciesLookup[
                                    userData.voicePolicy
                                ]
                            )?.id,
                            UPN: v.userPrincipalName,
                        },
                    });
                }
            }
            // Handle profile changes if needed
            return payloads;
        });
        console.log(newPayload);
        setPayloads(newPayload);
    };

    /**
     * validates / verifies licensed users against company's data from BE.
     * @param {*} param0
     * @returns users data with relevant errors.
     */
    const verifyCSVData = async ({ data }) => {
        const platformId = getPlatformId();
        if (
            platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
            platformId === '86394d2a-acdd-4923-8623-dcae7541e641'
        ) {
            const {
                availableUsers,
                availableNumbers,
                availableUsersLookup,
                availableLicensesByName,
                availableLicensesById,
                licenseUsage,
            } = companyData;
            //find who's managed by TCAP / others
            const [managedByOthers, managedByTCAP] = partition(
                data,
                (v) => v.attributesUpdateOnly,
            );
            /* for users managed by TCAP: */
            const { usersNotInCompany, usersNotLicensed, usersNotOK } =
                verifyUsers({
                    data: managedByTCAP,
                    availableUsers,
                });
            changeVerificationChecks(3);
            const {
                numbersNotOK,
                numbersNotInCompany,
                callValuesInconsistent,
            } = verifyNumbers({
                data: managedByTCAP,
                availableNumbers,
            });
            changeVerificationChecks(4);

            const {
                licenseNameInvalidUsers,
                licenseChildWithoutParentUsers,
                licenseCapacityUsers,
            } = verifyLicenses({
                data,
                availableLicensesByName,
                availableLicensesById,
                licenseUsage,
                availableUsersLookup,
            });
            changeVerificationChecks(5);

            /* for users managed by others: */
            const { managedByOthersInvalid } = verifyManagedBy({
                data: managedByOthers,
                availableUsersLookup,
            });
            const dataWithErrors = flagDataWithErrors({
                usersNotInCompany,
                usersNotLicensed,
                usersNotOK,
                numbersNotOK,
                numbersNotInCompany,
                callValuesInconsistent,
                managedByOthersInvalid,
                licenseNameInvalidUsers,
                licenseChildWithoutParentUsers,
                licenseCapacityUsers,
                data,
            });
            return { dataWithErrors };
        } else if (
            Cookies.get('selectedPlatformName') === 'ms_resource_account' ||
            Cookies.get('selectedPlatformName') === 'ms_resource_account_gcch'
        ) {
            const {
                availableUsers,
                availableNumbers,
                availableDPVPByService,
                availableUsersLookup,
                availableNumbersLookup,
                allNumbersLookup,
                availablePlansLookup,
            } = companyData;
            //find who's managed by TCAP / others
            const [managedByOthers, managedByTCAP] = partition(
                data,
                (v) => v.attributesUpdateOnly,
            );
            /* for users managed by TCAP: */
            const { usersNotInCompany, usersNotLicensed, usersNotOK } =
                verifyUsers({
                    data: managedByTCAP,
                    availableUsers,
                });
            changeVerificationChecks(3);
            const {
                numbersNotOK,
                numbersNotInCompany,
                callValuesInconsistent,
            } = verifyNumbers({
                data: managedByTCAP,
                availableNumbers,
            });
            changeVerificationChecks(4);
            //TODO CHANGEDP AND VP
            const {
                dialPlansNotInCompany,
                voicePoliciesNotInCompany,
                doNotNeedVP,
            } = await verifyDPVP({
                data: managedByTCAP,
                availableDPVPByService,
                availableNumbersLookup,
                allNumbersLookup,
                availableUsersLookup,
            });
            changeVerificationChecks(5);

            /* for users managed by others: */
            const { managedByOthersInvalid } = verifyManagedBy({
                data: managedByOthers,
                availableUsersLookup,
            });

            const dataWithErrors = flagDataWithErrors({
                usersNotInCompany,
                usersNotLicensed,
                usersNotOK,
                numbersNotOK,
                numbersNotInCompany,
                dialPlansNotInCompany,
                voicePoliciesNotInCompany,
                callValuesInconsistent,
                managedByOthersInvalid,
                doNotNeedVP,
                data,
            });
            return { dataWithErrors };
        } else {
            const {
                availableUsers,
                availableNumbers,
                availableDPVPByService,
                availableUsersLookup,
                availableNumbersLookup,
                allNumbersLookup,
                availablePlansLookup,
            } = companyData;
            //find who's managed by TCAP / others
            const [managedByOthers, managedByTCAP] = partition(
                data,
                (v) => v.attributesUpdateOnly,
            );
            /* for users managed by TCAP: */
            const { usersNotInCompany, usersNotLicensed, usersNotOK } =
                verifyUsers({
                    data: managedByTCAP,
                    availableUsers,
                });
            changeVerificationChecks(3);
            const {
                numbersNotOK,
                numbersNotInCompany,
                callValuesInconsistent,
            } = verifyNumbers({
                data: managedByTCAP,
                availableNumbers,
            });
            changeVerificationChecks(4);
            //TODO CHANGEDP AND VP
            const {
                dialPlansNotInCompany,
                voicePoliciesNotInCompany,
                doNotNeedVP,
                doNotNeedDP,
            } = await verifyDPVP({
                data: managedByTCAP,
                availableDPVPByService,
                availableNumbersLookup,
                allNumbersLookup,
                availableUsersLookup,
            });
            changeVerificationChecks(5);
            changeVerificationChecks(6);

            /* for users managed by others: */
            const { managedByOthersInvalid } = verifyManagedBy({
                data: managedByOthers,
                availableUsersLookup,
            });

            // check for user plans
            // changeVerificationProgresses(7);
            // const {
            //     invalidUserPlan,
            //     invalidPrefix,
            //     invalidNumberAllocated,
            //     invalidPlanPermissions,
            // } = verifyPlans({
            //     data: managedByTCAP,
            //     availablePlansLookup,
            // });
            // changeVerificationChecks(7);

            const dataWithErrors = flagDataWithErrors({
                usersNotInCompany,
                usersNotLicensed,
                usersNotOK,
                numbersNotOK,
                numbersNotInCompany,
                dialPlansNotInCompany,
                voicePoliciesNotInCompany,
                callValuesInconsistent,
                managedByOthersInvalid,
                doNotNeedDP,
                doNotNeedVP,
                // invalidUserPlan,
                // invalidPrefix,
                // invalidNumberAllocated,
                // invalidPlanPermissions,
                data,
            });
            return { dataWithErrors };
        }
    };

    /**
     * lists unlicensed users with changes, and those who aren't changed at all (ignored).
     * @param {*} param0
     * @returns {unlicensedUsersChanged, ignored, upnIsInvalid}
     */
    const findUnlicensedUsersAndIgnoredUsers = ({ data, originalDataJSON }) => {
        let ignored = differenceBy(originalDataJSON, data, 'userPrincipalName');

        const upnIsInvalid = ignored
            .filter((v) => !upnIsValidFilter(v))
            .map((v) => ({
                ...v,
                errors: [UPN_IS_INVALID_ERROR],
            }));

        // Remove unlicensedUsersInvalid check - accept all unlicensed users
        const unlicensedUsersInvalid = [];

        ignored = differenceBy(ignored, [...upnIsInvalid], 'userPrincipalName');

        const unlicensedUsersChanged = ignored.filter(
            (v) =>
                upnIsValidFilter(v) &&
                usersUnlicensedFilter(v) &&
                unlicensedUserIsChangedFilter(v),
        );
        ignored = differenceBy(
            ignored,
            unlicensedUsersChanged,
            'userPrincipalName',
        ).map((v) => ({
            ...v,
            ignored: true,
        }));
        return {
            unlicensedUsersChanged,
            ignored,
            upnIsInvalid,
            unlicensedUsersInvalid,
        };
    };

    /**
     * aggregates data with 0 errors with unlicensedUsersChanged into allSucceedData,
     * aggregates all data with errors & sort them by UPN,
     * aggregates all ignored users with allSucceedData & sort them by (verificationStatus, UPN)
     * @param {data, unlicensedUsersChanged, ignored} param0
     * @returns {allSucceedData}
     */
    const reportResult = ({
        data,
        unlicensedUsersChanged,
        ignored,
        upnIsInvalid,
        unlicensedUsersInvalid,
    }) => {
        console.log('=== REPORT RESULT START ===');
        console.log('Total data (licensed with changes):', data.length);
        console.log('Data with errors:', data.filter(haveErrorsFilter).length);
        console.log('Data without errors:', data.filter(noErrorsFilter).length);
        console.log('Total ignored users:', ignored.length);
        console.log('Unlicensed changed:', unlicensedUsersChanged.length);

        // Log all data users with their status
        data.forEach((v) => {
            const userAPIData =
                companyData.availableUsersLookup[v.userPrincipalName];
            console.log(
                `Data user: ${v.userPrincipalName}, status: ${userAPIData?.status}, errors: ${v.errors.length}`,
            );
        });

        // Filter out users with bad status from ignored list
        const ignoredWithBadStatus = ignored.filter((v) => {
            const userAPIData =
                companyData.availableUsersLookup[v.userPrincipalName];
            const hasBadStatus = userAPIData && usersNotOKFilter(userAPIData);
            if (hasBadStatus) {
                console.log(
                    'Found user with bad status in ignored:',
                    v.userPrincipalName,
                    'status:',
                    userAPIData.status,
                );
            }
            return hasBadStatus;
        });

        console.log(
            'Users with bad status in ignored:',
            ignoredWithBadStatus.length,
        );

        const validIgnored = ignored.filter((v) => {
            const userAPIData =
                companyData.availableUsersLookup[v.userPrincipalName];
            return !userAPIData || !usersNotOKFilter(userAPIData);
        });

        // Filter out users with bad status from data (even if they have no errors)
        const dataWithBadStatus = [];
        const dataFiltered = data.filter(noErrorsFilter).filter((v) => {
            const userAPIData =
                companyData.availableUsersLookup[v.userPrincipalName];
            const hasGoodStatus =
                !userAPIData || !usersNotOKFilter(userAPIData);
            if (!hasGoodStatus) {
                console.log(
                    'BLOCKING user from passed due to bad status:',
                    v.userPrincipalName,
                    'status:',
                    userAPIData?.status,
                );
                dataWithBadStatus.push({
                    ...v,
                    errors: [USER_NOT_OK_ERROR],
                });
            }
            return hasGoodStatus;
        });

        // Filter out unlicensed users with bad status
        const unlicensedWithBadStatus = [];
        const unlicensedFiltered = unlicensedUsersChanged.filter((v) => {
            const userAPIData =
                companyData.availableUsersLookup[v.userPrincipalName];
            const hasGoodStatus =
                !userAPIData || !usersNotOKFilter(userAPIData);
            if (!hasGoodStatus) {
                console.log(
                    'BLOCKING unlicensed user from passed due to bad status:',
                    v.userPrincipalName,
                    'status:',
                    userAPIData?.status,
                );
                unlicensedWithBadStatus.push({
                    ...v,
                    errors: [USER_NOT_OK_ERROR],
                });
            }
            return hasGoodStatus;
        });

        // Combine all failed validations (including all users with bad status)
        const failed = concat(
            upnIsInvalid,
            data.filter(haveErrorsFilter),
            unlicensedUsersInvalid,
            ignoredWithBadStatus.map((v) => ({
                ...v,
                errors: [USER_NOT_OK_ERROR],
            })),
            dataWithBadStatus,
            unlicensedWithBadStatus,
        );

        // All users without errors AND with good status
        const success = concat(dataFiltered, unlicensedFiltered);

        // Combine success with valid ignored users for the on-screen table,
        // which shows both Modified and Unchanged sections.
        const allDisplayData = concat(success, validIgnored);

        setVerificationResult((prev) => {
            const newVerificationResult = { ...prev };

            newVerificationResult.passed.data = orderBy(
                allDisplayData.map((v) => ({
                    ...v,
                    verificationStatus: v.ignored
                        ? IGNORED_MESSAGE
                        : SUCCESS_MESSAGE,
                    // Transform for display: managedBy 0 -> "TCAP", 1 -> "Other"
                    managedBy:
                        v.managedBy === 0
                            ? 'TCAP'
                            : v.managedBy === 1
                              ? 'Other'
                              : v.managedBy,
                })),
                ['userPrincipalName'],
                ['asc'],
            );

            newVerificationResult.failed.data = orderBy(
                failed.map((v) => ({
                    ...v,
                    // Transform for display: managedBy 0 -> "TCAP", 1 -> "Other"
                    managedBy:
                        v.managedBy === 0
                            ? 'TCAP'
                            : v.managedBy === 1
                              ? 'Other'
                              : v.managedBy,
                })),
                ['userPrincipalName'],
                ['asc'],
            );

            return newVerificationResult;
        });

        // Only genuinely changed users should be submitted to the API —
        // unchanged/ignored users are shown on screen but must not be
        // resubmitted.
        return { allSucceedData: success };
    };

    /**
     * flags each user data with relevant errors.
     * @param {*} param0 all errors + data
     * @returns data with errors field filled.
     */
    const flagDataWithErrors = ({
        usersNotInCompany,
        usersNotLicensed,
        usersNotOK,
        numbersNotOK,
        numbersNotInCompany,
        dialPlansNotInCompany,
        voicePoliciesNotInCompany,
        callValuesInconsistent,
        managedByOthersInvalid,
        doNotNeedDP,
        doNotNeedVP,
        licenseNameInvalidUsers = [],
        licenseChildWithoutParentUsers = [],
        licenseCapacityUsers = [],
        // invalidUserPlan = [],
        // invalidPrefix = [],
        // invalidNumberAllocated = [],
        // invalidPlanPermissions = [],
        data,
    }) => {
        console.log('=== FLAG DATA WITH ERRORS START ===');
        console.log('usersNotOK count:', usersNotOK.length);
        console.log(
            'usersNotOK users:',
            usersNotOK.map((u) => u.userPrincipalName),
        );

        const platformId = getPlatformId();
        if (
            platformId === '46140cdd-5134-4088-b474-9aad13987c9f' ||
            platformId === '86394d2a-acdd-4923-8623-dcae7541e641'
        ) {
            //give error message to every errors & concat them
            const errors = concat(
                usersNotInCompany.map((v) => ({ ...v, error: USER_DNE_ERROR })),
                usersNotLicensed.map((v) => ({
                    ...v,
                    error: USER_NOT_LICENSED_ERROR,
                })),
                usersNotOK.map((v) => ({ ...v, error: USER_NOT_OK_ERROR })),
                numbersNotOK.map((v) => ({ ...v, error: NUMBER_NOT_OK_ERROR })),
                numbersNotInCompany.map((v) => ({
                    ...v,
                    error: NUMBER_DNE_ERROR,
                })),
                managedByOthersInvalid.map((v) => ({
                    ...v,
                    error: MANAGED_BY_INVALID_ERROR,
                })),
                licenseNameInvalidUsers.map((v) => ({
                    ...v,
                    error: LICENSE_NAME_INVALID_ERROR,
                })),
                licenseChildWithoutParentUsers.map((v) => ({
                    ...v,
                    error: LICENSE_CHILD_WITHOUT_PARENT_ERROR,
                })),
                licenseCapacityUsers.map((v) => ({
                    ...v,
                    error: LICENSE_CAPACITY_ERROR,
                })),
            );

            console.log('Total errors to flag:', errors.length);
            console.log('Errors breakdown:', {
                usersNotInCompany: usersNotInCompany.length,
                usersNotLicensed: usersNotLicensed.length,
                usersNotOK: usersNotOK.length,
                numbersNotOK: numbersNotOK.length,
                numbersNotInCompany: numbersNotInCompany.length,
                managedByOthersInvalid: managedByOthersInvalid.length,
            });

            //temp lookup for user data
            const dataJSONTmp = {};

            //make a lookup of errors using UPN as key
            //TODO: change to lodash groupby
            errors.forEach(({ userPrincipalName, error }) => {
                console.log(`Adding error to ${userPrincipalName}: ${error}`);
                (
                    dataJSONTmp[userPrincipalName] ||
                    (dataJSONTmp[userPrincipalName] = [])
                ).push(error);
            });

            console.log('dataJSONTmp lookup:', dataJSONTmp);

            //append errors from temp lookup to each user data
            const newDataJSON = data.map((v) => ({
                ...v,
                errors: dataJSONTmp[v.userPrincipalName]
                    ? dataJSONTmp[v.userPrincipalName]
                    : [],
            }));

            console.log('After adding errors to data:');
            newDataJSON.forEach((v) => {
                console.log(
                    `User: ${v.userPrincipalName}, errors count: ${v.errors.length}, errors:`,
                    v.errors,
                );
            });

            setDataJSON(newDataJSON);

            return newDataJSON;
        } else if (
            Cookies.get('selectedPlatformName') === 'ms_resource_account' ||
            Cookies.get('selectedPlatformName') === 'ms_resource_account_gcch'
        ) {
            //give error message to every errors & concat them
            const errors = concat(
                usersNotInCompany.map((v) => ({ ...v, error: USER_DNE_ERROR })),
                usersNotLicensed.map((v) => ({
                    ...v,
                    error: USER_NOT_LICENSED_ERROR,
                })),
                usersNotOK.map((v) => ({ ...v, error: USER_NOT_OK_ERROR })),
                numbersNotOK.map((v) => ({ ...v, error: NUMBER_NOT_OK_ERROR })),
                numbersNotInCompany.map((v) => ({
                    ...v,
                    error: NUMBER_DNE_ERROR,
                })),

                voicePoliciesNotInCompany.map((v) => ({
                    ...v,
                    error: VP_DNE_ERROR,
                })),
                callValuesInconsistent.map((v) => ({
                    ...v,
                    error: CALL_VALUE_INCONSISTENT_ERROR,
                })),
                managedByOthersInvalid.map((v) => ({
                    ...v,
                    error: MANAGED_BY_INVALID_ERROR,
                })),
                doNotNeedVP.map((v) => ({
                    ...v,
                    error: NUMBER_IS_NOT_DR_ERROR,
                })),
            );
            //temp lookup for user data
            const dataJSONTmp = {};

            //make a lookup of errors using UPN as key
            //TODO: change to lodash groupby
            errors.forEach(({ userPrincipalName, error }) => {
                (
                    dataJSONTmp[userPrincipalName] ||
                    (dataJSONTmp[userPrincipalName] = [])
                ).push(error);
            });

            //append errors from temp lookup to each user data
            const newDataJSON = data.map((v) => ({
                ...v,
                errors: dataJSONTmp[v.userPrincipalName]
                    ? dataJSONTmp[v.userPrincipalName]
                    : [],
            }));

            setDataJSON(newDataJSON);

            return newDataJSON;
        } else {
            //give error message to every errors & concat them
            const errors = concat(
                usersNotInCompany.map((v) => ({ ...v, error: USER_DNE_ERROR })),
                usersNotLicensed.map((v) => ({
                    ...v,
                    error: USER_NOT_LICENSED_ERROR,
                })),
                usersNotOK.map((v) => ({ ...v, error: USER_NOT_OK_ERROR })),
                numbersNotOK.map((v) => ({ ...v, error: NUMBER_NOT_OK_ERROR })),
                numbersNotInCompany.map((v) => ({
                    ...v,
                    error: NUMBER_DNE_ERROR,
                })),
                dialPlansNotInCompany.map((v) => ({
                    ...v,
                    error: DIALPLAN_DNE_ERROR,
                })),
                voicePoliciesNotInCompany.map((v) => ({
                    ...v,
                    error: VP_DNE_ERROR,
                })),
                callValuesInconsistent.map((v) => ({
                    ...v,
                    error: CALL_VALUE_INCONSISTENT_ERROR,
                })),
                managedByOthersInvalid.map((v) => ({
                    ...v,
                    error: MANAGED_BY_INVALID_ERROR,
                })),
                doNotNeedDP.map((v) => ({
                    ...v,
                    error: NUMBER_IS_NOT_DR_ERROR,
                })),
                doNotNeedVP.map((v) => ({
                    ...v,
                    error: NUMBER_IS_NOT_DR_ERROR,
                })),
                // invalidUserPlan.map((v) => ({
                //     ...v,
                //     error: PLAN_NAME_ERROR,
                // })),
                // invalidPrefix.map((v) => ({
                //     ...v,
                //     error: PLAN_PREFIX_ERROR,
                // })),
                // invalidNumberAllocated.map((v) => ({
                //     ...v,
                //     error: PLAN_NUMBER_ERROR,
                // })),
                // invalidPlanPermissions.map((v) => ({
                //     ...v,
                //     error: PLAN_PERMISSION_ERROR,
                // })),
            );
            //temp lookup for user data
            const dataJSONTmp = {};

            //make a lookup of errors using UPN as key
            //TODO: change to lodash groupby
            errors.forEach(({ userPrincipalName, error }) => {
                (
                    dataJSONTmp[userPrincipalName] ||
                    (dataJSONTmp[userPrincipalName] = [])
                ).push(error);
            });

            //append errors from temp lookup to each user data
            const newDataJSON = data.map((v) => ({
                ...v,
                errors: dataJSONTmp[v.userPrincipalName]
                    ? dataJSONTmp[v.userPrincipalName]
                    : [],
            }));

            setDataJSON(newDataJSON);

            return newDataJSON;
        }
    };

    /**
     * Validate user per plan if allowed
     * @param {*} data users managed by TCAP.
     */
    // const verifyPlans = ({ data, availablePlansLookup }) => {
    //     let invalidUserPlan = [],
    //         invalidPrefix = [],
    //         invalidNumberAllocated = [],
    //         invalidPlanPermissions = [];
    //     if (currentCompanyData?.companyBillingSettings?.perUserPlan != 0) {
    //         data.filter((userData) => {
    //             // plan is changed and per user plan allows and telephone number is not empty
    //             if (
    //                 userData.isPlanChanged &&
    //                 currentCompanyData.companyBillingSettings.perUserPlan ==
    //                 2 &&
    //                 requiredScope < 40 &&
    //                 !isEmpty(userData.telephoneNumber)
    //             ) {
    //                 invalidPlanPermissions.push(userData);
    //                 return;
    //             } else {
    //                 var userPlan = isEqual(
    //                     userData.plan,
    //                     'Company Default Plan',
    //                 )
    //                     ? availablePlansLookup[currentCompanyData.defaultPlan]
    //                     : availablePlansLookup[userData.plan];

    //                 if (!isEmpty(userPlan)) {
    //                     var validNumberAllocated = !isEmpty(
    //                         userData.telephoneNumber,
    //                     );
    //                     if (!validNumberAllocated) {
    //                         invalidNumberAllocated.push(userData);
    //                         return;
    //                     }
    //                     var validPrefix = startsWith(
    //                         userData.telephoneNumber,
    //                         userPlan.countryPrefix,
    //                     );
    //                     if (!validPrefix) {
    //                         invalidPrefix.push(userData);
    //                         return;
    //                     }
    //                 } else {
    //                     if (!isEmpty(userData.telephoneNumber)) {
    //                         invalidUserPlan.push(userData);
    //                     }
    //                 }
    //             }
    //         });
    //     }
    //     return {
    //         invalidUserPlan,
    //         invalidPrefix,
    //         invalidNumberAllocated,
    //         invalidPlanPermissions,
    //     };
    // };

    /**
     * validate user is not trying to change their Telephone Number / Dial Plan / Voice Policy / Plan.
     * @param {*} param0 users managed by others.
     * @returns invalid users that are managed by others.
     */
    const verifyManagedBy = ({ data, availableUsersLookup }) => {
        const managedByOthersInvalid = data.filter((userData) => {
            const userAPIData =
                availableUsersLookup[userData.userPrincipalName];

            // Skip validation if user is in error state (status === 20)
            if (userAPIData && userAPIData.status === 20) {
                return false;
            }

            const usersAPIData = pick(
                userAPIData,
                FIELDS_SHOULD_NOT_CHANGE_MANAGED_BY_OTHERS,
            );
            const userDataPicked = pick(
                userData,
                FIELDS_SHOULD_NOT_CHANGE_MANAGED_BY_OTHERS,
            );

            const userDataUnchanged = dataIsUnchangedFilter(
                usersAPIData,
                userDataPicked,
            );

            // Skip validation if there are no changes to managed fields
            return !userDataUnchanged;
        });
        return { managedByOthersInvalid };
    };

    /**
     * validates user is : in OK status, is a legit current Company's user, is licensed.
     * @param {*} param0 CSV data & BE user's data to be checked against.
     * @returns users that don't pass validations.
     */
    const verifyUsers = ({ data, availableUsers }) => {
        const availableUsersNotLicensed = availableUsers.filter(
            usersUnlicensedFilter,
        );
        changeVerificationProgresses(3);
        const availableUsersNotOK = availableUsers.filter(usersNotOKFilter);
        console.log(
            'Users with status not 0 or 20:',
            availableUsersNotOK.map((u) => ({
                upn: u.userPrincipalName,
                status: u.status,
            })),
        );
        changeVerificationProgresses(3);
        const usersNotLicensed = intersectionBy(
            data,
            availableUsersNotLicensed,
            'userPrincipalName',
        );
        changeVerificationProgresses(3);
        const usersNotInCompany = differenceBy(
            data,
            availableUsers,
            'userPrincipalName',
        );
        changeVerificationProgresses(3);
        const usersNotOK = intersectionBy(
            data,
            availableUsersNotOK,
            'userPrincipalName',
        );
        console.log(
            'Users in CSV with bad status:',
            usersNotOK.map((u) => u.userPrincipalName),
        );
        changeVerificationProgresses(3);
        return { usersNotInCompany, usersNotLicensed, usersNotOK };
    };

    /**
     * validates number to be used is : in OK status, is a legit current Company's number, is consistent with call values.
     * @param {*} param0 CSV data & BE number's data to be checked against.
     * @returns numbers that don't pass validations.
     */
    const verifyNumbers = ({ data, availableNumbers }) => {
        const availableNumbersNotOK =
            availableNumbers.filter(numbersNotOKFilter);
        changeVerificationProgresses(4);
        const numbersNeedAssignment = data.filter(numbersNeedAssignmentFilter);
        const numbersActuallyChanged = numbersNeedAssignment.filter(
            (v) => v.isTelephoneNumberChanged,
        );
        let callValuesInconsistent;
        changeVerificationProgresses(4);
        if (
            Cookies.get('selectedPlatformName') === 'ms_resource_account' ||
            Cookies.get('selectedPlatformName') === 'ms_resource_account_gcch'
        ) {
            callValuesInconsistent = data.filter(
                (v) =>
                    v.isDRNumber && //needs to be number with DR Trunk Type
                    !callValueConsistentFilter(v), //call values arent consistent
            );
        } else {
            callValuesInconsistent = data.filter(
                (v) =>
                    v.isDRNumber && //needs to be number with DR Trunk Type
                    !callValuesConsistentFilter(v), //call values arent consistent
            );
        }

        changeVerificationProgresses(4);
        const numbersNotOK = intersectionBy(
            numbersNeedAssignment,
            availableNumbersNotOK,
            'telephoneNumber',
        );
        changeVerificationProgresses(4);
        const numbersNotInCompany = differenceBy(
            numbersActuallyChanged,
            availableNumbers,
            'telephoneNumber',
        );
        changeVerificationProgresses(4);

        return { numbersNotOK, numbersNotInCompany, callValuesInconsistent };
    };

    /**
     * Validates 8x8 licenses for a set of users.
     * Rules:
     *   1. License name must exist in the available catalog.
     *   2. A child license cannot be assigned without its parent.
     *   3. Adding a new license (not already on the user) requires available capacity > 0.
     */
    const verifyLicenses = ({
        data,
        availableLicensesByName,
        availableLicensesById,
        licenseUsage,
        availableUsersLookup,
    }) => {
        const licenseNameInvalidUsers = [];
        const licenseChildWithoutParentUsers = [];
        const licenseCapacityUsers = [];

        const licenseUsageByName = keyBy(licenseUsage, 'name');

        for (const userData of data) {
            const uploadedLicenseNames = userData.licenses || [];
            const currentUser =
                availableUsersLookup[userData.userPrincipalName];
            const currentLicenseNames = (
                currentUser?.userProfiles?.[0]?.licenses || []
            ).map((l) => l.name);

            // Rule 1: all uploaded license names must exist in catalog
            let hasInvalidName = false;
            for (const name of uploadedLicenseNames) {
                if (name && !availableLicensesByName[name]) {
                    hasInvalidName = true;
                    break;
                }
            }
            if (hasInvalidName) {
                licenseNameInvalidUsers.push(userData);
                continue;
            }

            // Rule 2: no orphaned child — every child in the uploaded list must have its parent also present
            let hasOrphanedChild = false;
            for (const name of uploadedLicenseNames) {
                if (!name) continue;
                const license = availableLicensesByName[name];
                if (!license || !license.parentID) continue;
                const parentLicense = availableLicensesById[license.parentID];
                if (
                    parentLicense &&
                    !uploadedLicenseNames.includes(parentLicense.name)
                ) {
                    hasOrphanedChild = true;
                    break;
                }
            }
            if (hasOrphanedChild) {
                licenseChildWithoutParentUsers.push(userData);
                continue;
            }

            // Rule 3: adding a license not already on the user requires available capacity
            const addedLicenses = uploadedLicenseNames.filter(
                (n) => n && !currentLicenseNames.includes(n),
            );
            let hasCapacityIssue = false;
            for (const addedName of addedLicenses) {
                const usage = licenseUsageByName[addedName];
                if (usage) {
                    const available =
                        (usage.allowedQuantity || 0) -
                        (usage.usedQuantity || 0);
                    if (available <= 0) {
                        hasCapacityIssue = true;
                        break;
                    }
                }
            }
            if (hasCapacityIssue) {
                licenseCapacityUsers.push(userData);
            }
        }

        return {
            licenseNameInvalidUsers,
            licenseChildWithoutParentUsers,
            licenseCapacityUsers,
        };
    };

    /**
     * validates DialPlans & VoicePolicies to be used is : a legit current Company's DialPlans & VoicePolicies from DR service.
     * @param {*} param0 CSV data & BE DialPlan & VoicePolicies data to be checked against.
     * @returns DialPlans & VoicePolicies that don't pass validations.
     */

    const verifyDPVP = async ({
        data,
        availableDPVPByService,
        availableNumbersLookup,
        // eslint-disable-next-line no-unused-vars
        allNumbersLookup,
        availableUsersLookup,
    }) => {
        if (
            Cookies.get('selectedPlatformName') === 'ms_resource_account' ||
            Cookies.get('selectedPlatformName') === 'ms_resource_account_gcch'
        ) {
            // Check if company has any GCCH service - if so, skip doNotNeedVP validation
            const hasGCCHService = Object.values(availableDPVPByService).some(
                (service) => service.serviceType === DR_GCCH_SERVICE_TYPE,
            );

            const voicePoliciesNeedUpdate = data.filter((v) =>
                stringNotEmptyFilter(v.voicePolicy),
            );
            // Skip validation entirely for GCCH, otherwise check if number needs VP
            const doNotNeedVP = hasGCCHService
                ? []
                : voicePoliciesNeedUpdate.filter((v) => !v.isDRNumber);
            let voicePoliciesNotInCompany = [];

            const [hasNewNumbers, hasOldNumbers] = partition(
                data.filter((v) => v.isDPOrVPChanged),
                (v) => v.isTelephoneNumberChanged,
            );

            for (const v of hasNewNumbers) {
                const serviceID =
                    availableNumbersLookup[v.telephoneNumber]?.serviceID || '';
                const serviceData =
                    serviceID && availableDPVPByService[serviceID];
                const vpExists =
                    serviceData && serviceData.voicePolicies[v.voicePolicy];

                if (!vpExists) {
                    voicePoliciesNotInCompany.push(v);
                }
            }
            try {
                const results = await verifyDPVPOldNumbers({
                    hasOldNumbers,
                    availableUsersLookup,
                });

                voicePoliciesNotInCompany = voicePoliciesNotInCompany.concat(
                    results.voicePoliciesNotInCompany,
                );
            } catch (e) {
                window.alert(
                    'you have some errors in verifying Voice Policies and Dial Plans' +
                        `${e}`,
                );
            } finally {
                changeVerificationProgresses(5);

                // eslint-disable-next-line no-unsafe-finally
                return {
                    voicePoliciesNotInCompany,
                    doNotNeedVP,
                };
            }
        } else {
            // Check if company has any GCCH service - if so, skip doNotNeedDP/VP validation
            const hasGCCHService = Object.values(availableDPVPByService).some(
                (service) => service.serviceType === DR_GCCH_SERVICE_TYPE,
            );

            const dialPlansNeedUpdate = data.filter((v) =>
                stringNotEmptyFilter(v.dialPlan),
            );
            // Skip validation entirely for GCCH, otherwise check if number needs DP
            const doNotNeedDP = hasGCCHService
                ? []
                : dialPlansNeedUpdate.filter((v) => !v.isDRNumber);

            const voicePoliciesNeedUpdate = data.filter((v) =>
                stringNotEmptyFilter(v.voicePolicy),
            );
            // Skip validation entirely for GCCH, otherwise check if number needs VP
            const doNotNeedVP = hasGCCHService
                ? []
                : voicePoliciesNeedUpdate.filter((v) => !v.isDRNumber);

            let dialPlansNotInCompany = [];
            let voicePoliciesNotInCompany = [];

            const [hasNewNumbers, hasOldNumbers] = partition(
                data.filter((v) => v.isDPOrVPChanged),
                (v) => v.isTelephoneNumberChanged,
            );

            for (const v of hasNewNumbers) {
                const serviceID =
                    availableNumbersLookup[v.telephoneNumber]?.serviceID || '';
                console.log(serviceID);
                const serviceData =
                    serviceID && availableDPVPByService[serviceID];
                console.log(serviceData);
                const dpExists =
                    serviceData && serviceData.dialPlans[v.dialPlan];
                console.log(dpExists);
                const vpExists =
                    serviceData && serviceData.voicePolicies[v.voicePolicy];
                if (!dpExists) {
                    dialPlansNotInCompany.push(v);
                }
                if (!vpExists) {
                    voicePoliciesNotInCompany.push(v);
                }
            }
            try {
                const results = await verifyDPVPOldNumbers({
                    hasOldNumbers,
                    availableUsersLookup,
                });
                dialPlansNotInCompany = dialPlansNotInCompany.concat(
                    results.dialPlansNotInCompany,
                );
                voicePoliciesNotInCompany = voicePoliciesNotInCompany.concat(
                    results.voicePoliciesNotInCompany,
                );
            } catch (e) {
                window.alert(
                    'you have some errors in verifying Voice Policies and Dial Plans' +
                        `${e}`,
                );
            } finally {
                changeVerificationProgresses(5);
                changeVerificationProgresses(5);
                changeVerificationProgresses(6);
                changeVerificationProgresses(6);
                // eslint-disable-next-line no-unsafe-finally
                return {
                    dialPlansNotInCompany,
                    voicePoliciesNotInCompany,
                    doNotNeedVP,
                    doNotNeedDP,
                };
            }
        }
    };

    const fetchAllOriginalUserNumbers = async (allOriginalUsers) => {
        let oldNumbersLookup = {},
            oldNumbers = [];
        try {
            console.log(allOriginalUsers);
            for await (const numberRes of allOriginalUsers.map((v) =>
                axios.get(
                    `/platformNumber/${v.userProfiles[0].numberID}/platform/${Cookies.get('selectedPlatformId')}`,
                ),
            )) {
                oldNumbersLookup[numberRes.data.id] = numberRes.data;
                oldNumbers.push(numberRes.data);
            }
            return Promise.resolve({
                oldNumbersLookup,
                oldNumbers,
            });
        } catch (e) {
            console.log('error fetching numbers data', { e });
            Promise.reject(e);
        }
    };
    const fetchOldDPVP = async (oldNumbers) => {
        const allServices = oldNumbers.map((v) => v.serviceID);
        let VPDPLookup = {};
        try {
            for await (const serviceRes of allServices.map((v) =>
                axios.get(`/service/${v}`),
            )) {
                VPDPLookup[serviceRes.data.id] = serviceRes.data;
            }
            return Promise.resolve(VPDPLookup);
        } catch (e) {
            console.log('error fetching numbers data', { e });
            Promise.reject(e);
        }
    };

    const verifyDPVPOldNumbers = async ({
        hasOldNumbers,
        availableUsersLookup,
    }) => {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            if (
                Cookies.get('selectedPlatformName') === 'ms_resource_account' ||
                Cookies.get('selectedPlatformName') ===
                    'ms_resource_account_gcch'
            ) {
                const allOriginalUsers = hasOldNumbers.map(
                    (v) => availableUsersLookup[v.userPrincipalName],
                );
                console.log(availableUsersLookup);
                try {
                    const voicePoliciesNotInCompany = [];
                    const { oldNumbersLookup, oldNumbers } =
                        await fetchAllOriginalUserNumbers(allOriginalUsers);
                    const availableDPVPByService =
                        await fetchOldDPVP(oldNumbers);
                    for (const v of hasOldNumbers) {
                        console.log(v);
                        const originalData =
                            availableUsersLookup[v.userPrincipalName];
                        const serviceID =
                            originalData.telephoneNumber &&
                            oldNumbersLookup[originalData.numberID]?.serviceID;
                        const serviceData =
                            serviceID && availableDPVPByService[serviceID];

                        const vpExists =
                            serviceData &&
                            serviceData.voicePolicies.find(
                                (existingVP) =>
                                    existingVP.name === v.voicePolicy,
                            );

                        if (!vpExists) {
                            voicePoliciesNotInCompany.push(v);
                        }
                    }
                    resolve({ voicePoliciesNotInCompany });
                } catch (e) {
                    console.log('catch reject', { e });
                    reject(e);
                }
            } else {
                const allOriginalUsers = hasOldNumbers.map(
                    (v) => availableUsersLookup[v.userPrincipalName],
                );
                console.log(availableUsersLookup);
                try {
                    const dialPlansNotInCompany = [],
                        voicePoliciesNotInCompany = [];
                    const { oldNumbersLookup, oldNumbers } =
                        await fetchAllOriginalUserNumbers(allOriginalUsers);
                    const availableDPVPByService =
                        await fetchOldDPVP(oldNumbers);
                    for (const v of hasOldNumbers) {
                        console.log(v);
                        const originalData =
                            availableUsersLookup[v.userPrincipalName];
                        const serviceID =
                            originalData.telephoneNumber &&
                            oldNumbersLookup[originalData.numberID]?.serviceID;
                        const serviceData =
                            serviceID && availableDPVPByService[serviceID];
                        const dpExists =
                            serviceData &&
                            serviceData.dialPlans.find(
                                (existingDP) => existingDP.name === v.dialPlan,
                            );
                        const vpExists =
                            serviceData &&
                            serviceData.voicePolicies.find(
                                (existingVP) =>
                                    existingVP.name === v.voicePolicy,
                            );

                        if (!dpExists) {
                            dialPlansNotInCompany.push(v);
                        }
                        if (!vpExists) {
                            voicePoliciesNotInCompany.push(v);
                        }
                    }
                    resolve({
                        dialPlansNotInCompany,
                        voicePoliciesNotInCompany,
                    });
                } catch (e) {
                    console.log('catch reject', { e });
                    reject(e);
                }
            }
        });
    };

    /**
     * use papaparse to convert CSV -> JSON (with webworker).
     * @param {*} data CSV data to be converted to JSON.
     * @returns JSON data
     */
    // const convertCSVDataToJSON = async (data) => {
    //     return new Promise((resolve) => {
    //         const papaparseConfig = {
    //             dynamicTyping: true, //for numbers type
    //             header: true, //CSV has headers
    //             worker: true, //use worker thread
    //             complete: function (results) {
    //                 return resolve(results);
    //             },
    //         };
    //         Papa.parse(data, papaparseConfig);
    //     });
    // };
    const convertCSVDataToJSON = async (data) => {
        return new Promise((resolve) => {
            const papaparseConfig = {
                dynamicTyping: false, // Keep everything as strings to preserve attribute values
                header: true,
                worker: true, // Keep using worker
                skipEmptyLines: true,
                // transformHeader: (header) => header.trim(), // <-- REMOVE THIS LINE for worker compatibility
                complete: function (results) {
                    console.log('Parsed results before processing:', results);

                    // Manually trim headers after parsing if needed (though PapaParse usually handles basic whitespace)
                    // If headers still have issues, you might need to process results.meta.fields here
                    // For example: const trimmedHeaders = results.meta.fields.map(h => h.trim());

                    const sanitizeCSVField = (value) => {
                        if (typeof value !== 'string' || value.length === 0)
                            return value;
                        const firstChar = value.charAt(0);
                        // Check for =, +, -, @, tab, carriage return, and newline
                        if (
                            firstChar === '=' ||
                            firstChar === '+' ||
                            firstChar === '-' ||
                            firstChar === '@' ||
                            firstChar === '\t' ||
                            firstChar === '\r' ||
                            firstChar === '\n' ||
                            firstChar === String.fromCharCode(9) || // tab
                            firstChar === String.fromCharCode(10) || // newline (LF)
                            firstChar === String.fromCharCode(13) // carriage return (CR)
                        ) {
                            return "'" + value;
                        }
                        return value;
                    };
                    const processedData = results.data.map((row) => {
                        const newRow = { ...row };
                        Object.keys(newRow).forEach((key) => {
                            if (typeof newRow[key] === 'string') {
                                newRow[key] = sanitizeCSVField(newRow[key]);
                            }
                        });
                        // Access headers safely, assuming PapaParse trimmed them correctly or handle potential whitespace

                        return newRow;
                    });

                    results.data = processedData;
                    console.log('Processed JSON results:', results);
                    return resolve(results);
                },
            };
            Papa.parse(data, papaparseConfig);
        });
    };

    /**
     * exports records that failed validation (Ignored Records), matching the
     * columns shown in the on-screen table and reflecting the selected platform.
     * @param {Array} failedColumns - the same {title, field} columns rendered
     *   in the on-screen "Ignored Records" grid (see getUserColumns).
     */
    const handleExportInvalidData = (failedColumns) => {
        const platformName = Cookies.get('selectedPlatformName');
        const fieldsMap = columnsToFieldsMap(failedColumns);
        const invalidData = (verificationResult.failed.data || []).map((user) =>
            Object.keys(fieldsMap).reduce((acc, key) => {
                const value = user[key];
                acc[fieldsMap[key]] =
                    key === 'errors' && Array.isArray(value)
                        ? value.join('; ')
                        : (value ?? '');
                return acc;
            }, {}),
        );
        const res = Papa.unparse(invalidData);
        downloadCSV(
            res,
            buildUsersFilename(
                currentCompanyData?.name,
                platformName,
                'Ignored',
                'Validation',
            ),
        );
    };

    /**
     * exports records that passed validation as modifications, matching the
     * columns shown in the on-screen table and reflecting the selected platform.
     * @param {Array} passedColumns - the same {title, field} columns rendered
     *   in the on-screen "Modified Records" grid (see getUserColumns).
     */
    const handleExportModifiedData = (passedColumns) => {
        const platformName = Cookies.get('selectedPlatformName');
        const fieldsMap = columnsToFieldsMap(passedColumns);
        const modified = (verificationResult.passed.data || []).filter(
            (v) => v.verificationStatus === SUCCESS_MESSAGE,
        );
        const exportData = modified.map((user) =>
            Object.keys(fieldsMap).reduce((acc, key) => {
                acc[fieldsMap[key]] = user[key] ?? '';
                return acc;
            }, {}),
        );
        const res = Papa.unparse(exportData);
        downloadCSV(
            res,
            buildUsersFilename(
                currentCompanyData?.name,
                platformName,
                'Modified',
                'Validation',
            ),
        );
    };

    /**
     * exports records that were unchanged, matching the columns shown in the
     * on-screen table and reflecting the selected platform.
     * @param {Array} passedColumns - the same {title, field} columns rendered
     *   in the on-screen "Unchanged Records" grid (see getUserColumns).
     */
    const handleExportUnchangedData = (passedColumns) => {
        const platformName = Cookies.get('selectedPlatformName');
        const fieldsMap = columnsToFieldsMap(passedColumns);
        const unchanged = (verificationResult.passed.data || []).filter(
            (v) => v.verificationStatus === IGNORED_MESSAGE,
        );
        const exportData = unchanged.map((user) =>
            Object.keys(fieldsMap).reduce((acc, key) => {
                acc[fieldsMap[key]] = user[key] ?? '';
                return acc;
            }, {}),
        );
        const res = Papa.unparse(exportData);
        downloadCSV(
            res,
            buildUsersFilename(
                currentCompanyData?.name,
                platformName,
                'Unchanged',
                'Validation',
            ),
        );
    };

    return {
        handleUploadUsersData,
        uploadData,
        verificationChecks,
        handleExportInvalidData,
        handleExportModifiedData,
        handleExportUnchangedData,
        loading,
        payloads,
        verificationResult,
        handleReset,
        error,
        csvLimit,
    };
}
